import type { DocPageContent } from '../docs-content'

const page = {
  order: 17,
  slug: 'local-ai',
  title: 'Herramientas de IA Local',
  description:
    'Conversión de texto a voz, generación de música y transcripción, con almacenamiento en caché local de modelos.',
  category: 'Herramientas Creativas',
  related: ['scene-browser', 'text-captions-subtitles', 'audio'],
  sections: [
    {
      title: 'Cómo funciona la IA local',
      blocks: [
        {
          kind: 'list',
          items: [
            'Las herramientas de IA se ejecutan usando tu propio hardware.',
            'La primera vez que usas un modelo, se descarga una vez y luego se almacena en caché localmente para reutilizarlo.',
            'Una píldora de estado muestra los estados de carga, activo, listo y error mientras se ejecuta un modelo.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Algunas herramientas requieren soporte WebGPU. La primera descarga del modelo puede ser grande.',
        },
      ],
    },
    {
      title: 'Texto a voz',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Abre la pestaña **IA** y expande **Texto a Voz**, luego escribe o pega tu guion.',
            'Elige un motor (consulta la tabla), luego selecciona una voz y, cuando sea compatible, velocidad, idioma y etiquetas expresivas.',
            'Previsualiza el resultado y luego **Guardar e Insertar** para colocar audio vinculado en el cabezal de reproducción, o **Guardar en Biblioteca**.',
          ],
        },
        {
          kind: 'table',
          headers: ['Motor', 'Idiomas', 'Se ejecuta en'],
          rows: [
            ['Kokoro', 'Inglés', 'WebGPU'],
            ['MOSS Nano', '20 idiomas', 'CPU'],
            ['Supertonic 3', '31 idiomas', 'ONNX local'],
          ],
        },
      ],
    },
    {
      title: 'Generación de música',
      blocks: [
        {
          kind: 'list',
          items: [
            'Expande **Generación de Música** y describe la pista con un mensaje, o comienza desde un preajuste como Lo-fi chill o Upbeat EDM.',
            'Establece la duración en segundos y luego **Generar Música**; la primera ejecución descarga el modelo y lo almacena en caché.',
            'Previsualiza el clip y luego **Guardar e Insertar** en la línea de tiempo o **Guardar en Biblioteca**.',
          ],
        },
      ],
    },
    {
      title: 'Transcripción',
      blocks: [
        {
          kind: 'list',
          items: [
            'La transcripción se encuentra en la biblioteca de Medios y en el flujo de trabajo de Transcripción, separada de la pestaña IA.',
            '**Parakeet** es el motor rápido por defecto y cubre muchos idiomas europeos.',
            'Los modelos Whisper (Tiny, Base, Small, Large v3 Turbo) también están disponibles, y CachoCut recurre a Whisper cuando es necesario.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Una transcripción permite subtítulos, búsqueda en transcripciones y eliminación de palabras de relleno — genérala pronto.',
        },
      ],
    },
    {
      title: 'Gestionar modelos y entornos de ejecución',
      blocks: [
        {
          kind: 'list',
          items: [
            'Abre los ajustes de **Almacenamiento** para gestionar la IA local.',
            'Usa **Caché de Modelos de IA Local** para inspeccionar o borrar modelos descargados — útil si una descarga está corrupta.',
            'Usa **Descargar Modelos Locales** para liberar entornos de ejecución y liberar memoria.',
            'Los trabajos largos se pueden cancelar si ya no necesitas el resultado.',
          ],
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page